/**
 * The Tasks page's URL state.
 *
 * Same rule as Contacts: the list a person is looking at IS the address. "Alle oppgaver gruppert
 * etter festival, bare de som er blokkert" has to be a link a colleague can be sent, and a
 * reload has to land on it rather than on the unfiltered default. So the scope toggle, the
 * grouping, the direction and the status filter write to the address bar instead of to a
 * `useState`, and this module — not a component — decides what a valid Tasks URL is.
 *
 * Absent means default. `?scope=mine`, `?group=dueDate` and `?dir=asc` are never written, so the
 * plain `/tasks` a person types is the same location the menu links to and the router does not
 * treat them as two.
 *
 * ---------------------------------------------------------------------------------------------
 * WHY THERE IS ONE PARAMETER FOR GROUPING AND ORDER, AND NOT TWO
 *
 * `packages/shared/src/contracts/tasks.ts` is explicit about it, and the reason is not tidiness:
 *
 *   > A client that groups only the page it is holding is telling the same lie as a client that
 *   > sorts the page it is holding.
 *
 * The grouping happens in the `ORDER BY`, so rows of one group arrive adjacent and the client's
 * only job is to draw a header when the key changes between two rows. That makes "grouped by
 * festival" and "ordered by festival" the same request, and a `groupBy` beside a `sort` would be
 * two parameters that can disagree — `?sort=created&groupBy=department` is a grouping the
 * database cannot deliver adjacently.
 *
 * So there is one parameter, `group`, and `sortOf()` turns it into the sort key the endpoint
 * takes. A click on a sortable column header writes the same parameter, which is why clicking
 * "Festival" both orders and groups by it: in this list those are one act, and pretending
 * otherwise would need a control the API cannot honour.
 *
 * `none` is the exception, and it is a client-side one. It asks for the same `due` order as
 * `dueDate` and simply draws no headers — a flat list by deadline. It is the one grouping choice
 * that is genuinely about rendering, so it is the one that does not change the request.
 * ---------------------------------------------------------------------------------------------
 */
import {
  ORGANISATION_ONLY,
  TASK_PRIORITIES,
  TASK_STATUSES,
  type SortDirection,
  type TaskPriority,
  type TaskSortKey,
  type TaskStatus,
} from '@tvitvi/shared';

import type { TableSort } from '@/components/ui/Table';

/**
 * How the list is cut into groups — the plan's "gruppert (etter frist, festival eller gruppe)",
 * plus the flat list.
 *
 * The names are the ones the vocabulary uses (`tasks.groupBy.*` in `nb.json`), not the API's
 * sort keys, because this is what a person reads in the address bar.
 */
export const TASK_GROUPINGS = ['dueDate', 'festival', 'group', 'none'] as const;
export type TaskGrouping = (typeof TASK_GROUPINGS)[number];

export const DEFAULT_GROUPING: TaskGrouping = 'dueDate';

/**
 * The order each grouping asks the API for.
 *
 * `satisfies Record<TaskGrouping, TaskSortKey>` is the load-bearing part: a key the endpoint
 * does not accept cannot be written here, so the page can never ask for a sort the API would
 * reject — and a contract that dropped one would fail to compile rather than 400 at runtime.
 *
 * WHY `created` IS NOT REACHABLE. `GET /v1/tasks` accepts it and this table has no column for
 * it. The only sort affordance in this product is a column header — `Table` owns that mechanism
 * and there is deliberately not a second one — so `created` would be a list ordered by a value
 * the reader cannot see, which is the same defect Contacts records for its own `created`. The
 * flat list is `due` for that reason: "no grouping" is not "no order".
 */
const GROUPING_SORT = {
  dueDate: 'due',
  festival: 'edition',
  group: 'department',
  none: 'due',
} as const satisfies Record<TaskGrouping, TaskSortKey>;

/**
 * The table column each grouping is expressed by, so a header click can write the grouping and
 * the sorted column can wear `aria-sort` and the arrow.
 *
 * `none` is absent on purpose: it is not a column, it is the absence of headers, and it is
 * reachable only from the "Grupper etter" control.
 */
export const GROUPING_COLUMNS = {
  dueDate: 'dueAt',
  festival: 'edition',
  group: 'department',
} as const satisfies Partial<Record<TaskGrouping, string>>;

export type GroupingColumn = (typeof GROUPING_COLUMNS)[keyof typeof GROUPING_COLUMNS];

/**
 * List or board — `PAGES.md`'s "Visninger: liste og tavle (kolonner = status)".
 *
 * IN THE ADDRESS BAR, like the scope, the grouping and the status filter. A view is something a
 * person picks once and works in for an afternoon, and a link to "the board with the overdue
 * filter on" is a link a colleague can be sent. `useState` would lose it on every reload.
 *
 * `list` IS THE DEFAULT AND IS NEVER WRITTEN. Same rule as every other parameter here: the
 * default state has a clean URL, so the address bar only ever says what somebody chose.
 *
 * KOI DEL 1 LA TIL `timeline`, og ikke `calendar` eller `map` ved siden av den: en visning man kan
 * velge og som svarer med ingenting er verre enn ingen visning.
 *
 * DEL 2 LA TIL `map`, fordi den nå tegner noe. Ordren er eksplisitt — «Kart-visning i Oppgaver
 * (segmentet) … samme kart filtrert av treet og filtrene» — og det er en VISNING og ikke en rute:
 * de samme radene lista viser, tegnet som prikker. `calendar` står fortsatt igjen, og er Del 3 sin
 * egen side.
 *
 * KARTSEGMENTET TILBYS BARE DER DET FINNES ET ÅR. Et kart er alltid ett års kart (0222), så
 * organisasjonens egen `/tasks` uten årgangsfilter har ingenting å tegne — `TasksPage` utelater
 * valget der framfor å tilby et tomt kart.
 */
export const TASK_VIEWS = ['list', 'board', 'timeline', 'map'] as const;
export type TaskView = (typeof TASK_VIEWS)[number];

export const DEFAULT_VIEW: TaskView = 'list';

/**
 * Whose tasks. `mine` is the default because that is what the page is for (`tasks.subtitle`).
 *
 * `myday` ER EN TREDJE VERDI PÅ DEN SAMME BRYTEREN og ikke et filter ved siden av, fordi det er det
 * samme spørsmålet en gang til: HVILKE oppgaver ser jeg på. «Mine» er alt som er mitt, «Min dag» er
 * det jeg har valgt ut for i dag, «Alle» er husets. Tre knapper, én bryter, og den som trykker
 * «Min dag» skal ikke måtte skru av «Mine» først.
 */
export const TASK_SCOPES = ['mine', 'all', 'myday'] as const;
export type TaskScope = (typeof TASK_SCOPES)[number];

export const DEFAULT_SCOPE: TaskScope = 'mine';
const DEFAULT_DIRECTION: SortDirection = 'asc';

/**
 * What `validateSearch` produces and what `navigate({ search })` must be handed back.
 *
 * `status` is one comma-separated parameter rather than a repeated one because that is the
 * shorter URL and `statusListQuerySchema` accepts exactly this form — the link a person copies
 * out of the address bar is the query the server receives.
 */
export interface TasksSearch {
  scope?: TaskScope;
  view?: TaskView;
  group?: TaskGrouping;
  dir?: SortDirection;
  status?: string;
  /**
   * The other three filters `PAGES.md` asks Tasks [F1] for: «prioritet, arrangement, avdeling» —
   * and «forfalt», which is the fourth.
   *
   * `priority` is a comma list for the same reason `status` is. `edition` and `dept` are single
   * ids and are named for what the reader sees rather than for the API's field: the address bar is
   * read by people, and `?edition=` beside `?dept=` is a pair somebody can edit by hand.
   *
   * `overdue` is a BOOLEAN and never the string `'true'`, and a browser found the difference.
   *
   * The router round-trips search values through JSON, so a value that would parse as something
   * other than a string has to be quoted to survive as one: `overdue: 'true'` was written to the
   * address bar as `?overdue=%22true%22`. Ugly, and worse than ugly — a person who typed the
   * obvious `?overdue=true` by hand got the boolean, which the parser was not looking for, and the
   * filter silently did nothing. As a boolean it writes `?overdue=true` and reads both.
   *
   * `false` is never written. A `?overdue=false` in a link is a filter that says it is off, which
   * is noise in an address that is supposed to say what somebody chose.
   */
  priority?: string;
  /**
   * HVILKE ARRANGEMENTER MAN VIL SE (QA13 runde 2, #39). Utelatt betyr alle.
   *
   * Atle ba om den samme avkryssingslista i Oppgaver som i org-kalenderen, og det er derfor en
   * LISTE og ikke lenger ett år. `ORGANISATION_ONLY` er ett av valgene — en oppgave uten årgang
   * er like mye en del av utvalget som en festival er, og null kan ikke krysses av.
   *
   * DEN GAMLE `?edition=` LEVER VIDERE SOM INNGANG. Den sto i lenker folk har sendt hverandre, og
   * en adresse som stille slutter å filtrere er verre enn en som feiler: parseren folder den inn
   * som en liste med ett element, og skriver den aldri tilbake.
   */
  editions?: readonly string[];
  dept?: string;
  overdue?: boolean;
  /**
   * FRITEKSTSØKET, I ADRESSEN som alle de andre filtrene på denne siden.
   *
   * Atle, 9. september: «ALLE TABELLER MÅ KUNNE SØKES OG SORTERES!!!» Oppgavelista kunne grupperes
   * og filtreres på fem måter og ikke søkes — og i uka før en festival er den hundrevis lang.
   */
  q?: string;
  /**
   * KOI Del 1 — etikettfilteret og den åpne oppgaven.
   *
   * `label` er én id, som `edition` og `dept`, og av samme grunn: en chip man setter og jobber
   * inne i. Flere samtidig ville vært et OG eller et ELLER ingen kan lese av en pille.
   *
   * `open` ER PANELET, OG DET ER DERFOR DET STÅR I ADRESSEN. Ordren beholder `/tasks/:id` som
   * dyplenke «og åpner lista med panelet oppe» — så panelet må kunne uttrykkes som en adresse,
   * ellers er de to tingene to forskjellige sider. En lenke til «lista, med denne oppgaven åpen»
   * er også det en kollega får tilsendt når noen spør om en oppgave.
   */
  label?: string;
  open?: string;
}

/** Den samme grensen kontrakten setter, så et for langt søk aldri blir en 400. */
const MAX_SEARCH = 120;

/**
 * The PARSE surface stays the full enum, deliberately wider than what the chips offer.
 *
 * `cancelled` is not a choice anywhere in the UI (decision 0013), but it is still a value the
 * column can hold, so a URL that names it — a bookmark from before, a link somebody pasted, a
 * value that comes back one day — must round-trip rather than being silently dropped. Narrowing a
 * parser to match a picker is how a stored row becomes unreachable.
 */
const MAX_STATUSES = TASK_STATUSES.length;

function isStatus(value: string): value is TaskStatus {
  return (TASK_STATUSES as readonly string[]).includes(value);
}

/** Accepts the comma form and the array form; returns known statuses only, de-duplicated. */
function parseStatuses(value: unknown): readonly TaskStatus[] {
  const parts = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : ([] as unknown[]);
  const found = parts.map((part) => (typeof part === 'string' ? part.trim() : '')).filter(isStatus);
  return [...new Set(found)].slice(0, MAX_STATUSES);
}

const MAX_PRIORITIES = TASK_PRIORITIES.length;

function isPriority(value: string): value is TaskPriority {
  return (TASK_PRIORITIES as readonly string[]).includes(value);
}

/** The same for priority. Every one of the four is a choice the chips offer, unlike `cancelled`. */
function parsePriorities(value: unknown): readonly TaskPriority[] {
  const parts = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : ([] as unknown[]);
  const found = parts
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(isPriority);
  return [...new Set(found)].slice(0, MAX_PRIORITIES);
}

/**
 * An id in the address bar, checked for SHAPE and not for existence.
 *
 * A uuid that names nothing answers with an empty list, which is the honest outcome for a link to
 * an årgang somebody deleted. What must not reach the API is a value that is not a uuid at all:
 * that is a 400 on a page load, and a hand-edited URL should degrade to a list rather than to an
 * error page.
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function parseId(value: unknown): string | undefined {
  return typeof value === 'string' && UUID.test(value) ? value : undefined;
}

/**
 * Arrangementutvalget, enten det kom som en kommaliste eller som et array (ruteren gir begge).
 *
 * `ORGANISATION_ONLY` slipper gjennom ved siden av uuid-ene fordi det ER et av valgene, og duper
 * fjernes: `?editions=a,a` er én avkryssing, ikke to.
 */
function parseEditionList(value: unknown): readonly string[] {
  const raw = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
  const kept = raw
    .map((one) => (typeof one === 'string' ? one.trim() : ''))
    .filter((one) => one === ORGANISATION_ONLY || UUID.test(one));
  return [...new Set(kept)];
}

/**
 * Validated at the edge of the router, like `safeRedirect` is: no component downstream ever sees
 * a grouping that is not one of four values, or a status the API would answer 400 for.
 *
 * An unknown value is DROPPED rather than corrected, so `?group=colour` renders the default list
 * instead of an error page. A URL is something people edit by hand and something old links
 * carry; the page it names should still open.
 */
export function validateTasksSearch(raw: Record<string, unknown>): TasksSearch {
  const search: TasksSearch = {};

  const scope = raw['scope'];
  if (typeof scope === 'string' && (TASK_SCOPES as readonly string[]).includes(scope)) {
    if (scope !== DEFAULT_SCOPE) search.scope = scope as TaskScope;
  }

  const view = raw['view'];
  if (typeof view === 'string' && (TASK_VIEWS as readonly string[]).includes(view)) {
    if (view !== DEFAULT_VIEW) search.view = view as TaskView;
  }

  const group = raw['group'];
  if (typeof group === 'string' && (TASK_GROUPINGS as readonly string[]).includes(group)) {
    if (group !== DEFAULT_GROUPING) search.group = group as TaskGrouping;
  }

  if (raw['dir'] === 'desc') search.dir = 'desc';

  const statuses = parseStatuses(raw['status']);
  if (statuses.length > 0 && statuses.length < MAX_STATUSES) search.status = statuses.join(',');

  const priorities = parsePriorities(raw['priority']);
  if (priorities.length > 0 && priorities.length < MAX_PRIORITIES) {
    search.priority = priorities.join(',');
  }

  /*
   * ARRANGEMENTUTVALGET (#39), etter samme regel som statusene: det som er gyldig beholdes, og
   * resten faller bort framfor å tømme lista. En id som ikke finnes filtrerer bort alt den ikke
   * matcher — samme oppførsel som en slettet årgang — og å slå den opp her ville krevd et
   * nettverkskall inne i ruterens kant.
   *
   * `?edition=` FØRST, som den gamle inngangen den er. Står begge, vinner lista: den er det
   * kontrollen på skjermen skriver, og den gamle kan da bare være en rest i en lenke.
   */
  const legacy = parseId(raw['edition']);
  const editions = parseEditionList(raw['editions']);
  if (editions.length > 0) search.editions = editions;
  else if (legacy !== undefined) search.editions = [legacy];

  const dept = parseId(raw['dept']);
  if (dept !== undefined) search.dept = dept;

  // Both spellings are accepted: the boolean the router round-trips, and the `true` a person
  // types into the address bar. Anything else — including `false` — is no filter at all.
  if (raw['overdue'] === true || raw['overdue'] === 'true') search.overdue = true;

  const q = raw['q'];
  if (typeof q === 'string' && q.trim() !== '') search.q = q.trim().slice(0, MAX_SEARCH);

  const label = parseId(raw['label']);
  if (label !== undefined) search.label = label;

  const open = parseId(raw['open']);
  if (open !== undefined) search.open = open;

  return search;
}

export function scopeOf(search: TasksSearch): TaskScope {
  return search.scope ?? DEFAULT_SCOPE;
}

export function groupingOf(search: TasksSearch): TaskGrouping {
  return search.group ?? DEFAULT_GROUPING;
}

export function viewOf(search: TasksSearch): TaskView {
  return search.view ?? DEFAULT_VIEW;
}

/**
 * KARTET ER ETT ÅRS KART (0222, og svaret på L-3) — så uten et år FINNES ikke visningen.
 *
 * `TasksPage` utelot allerede segmentet på organisasjonens egen `/tasks`, men bare segmentet: en
 * adresse med `?view=map` skrevet for hånd, eller en lenke laget inne i en årgang og limt inn
 * utenfor, tegnet lista med INGEN segmenter trykket inn. Regelen bor her framfor i komponenten,
 * fordi den samme setningen må gjelde to steder — hva som tegnes, og hva `go()` skriver i
 * adressen — og to kopier av den ville før eller siden vært uenige.
 *
 * `hasEdition` er «finnes det et år», uansett om det kom fra ruten eller fra årgangsfilteret.
 */
export function viewFor(search: TasksSearch, hasEdition: boolean): TaskView {
  const view = viewOf(search);
  return view === 'map' && !hasEdition ? DEFAULT_VIEW : view;
}

/**
 * Den samme regelen, skrevet tilbake i adressen framfor bare lest ut av den.
 *
 * `viewFor` avgjør hva som TEGNES; denne avgjør hva som STÅR. De trengs begge fordi året kan
 * forsvinne mens man ser på kartet — «Fjern filtre» og et nullstilt årgangsfilter gjør nettopp det
 * — og et `?view=map` som ble stående igjen ville vært en visning velgeren ikke lenger tilbyr, i en
 * lenke noen kan sende videre. Ingenting annet i søket røres: dette er ikke et bytte av visning,
 * det er den samme visningen fortalt riktig.
 */
export function normalizeView(search: TasksSearch, hasEdition: boolean): TasksSearch {
  const effective = viewFor(search, hasEdition);
  if (effective === viewOf(search)) return search;
  const { view: _view, ...rest } = search;
  return effective === DEFAULT_VIEW ? rest : { ...rest, view: effective };
}

/**
 * Switching view keeps every other narrowing, and DROPS the grouping.
 *
 * The board's columns ARE the grouping — `PAGES.md`: "kolonner = status" — so a `?group=festival`
 * carried into it would be a parameter with nothing to do, sitting in the address bar waiting to
 * surprise somebody who switched back. Everything that still means something on both views — whose
 * tasks, which statuses — is kept, because switching view is not switching subject.
 *
 * `view` MÅ UT AV RESTEN, og at den ikke var det ER #19 (QA-runde 12, P1).
 *
 * Linja plukket `group` og `dir` ut og lot `view` bli igjen. Å bytte til en ikke-standard visning
 * overskrev den på veien ut (`{ ...rest, view }`), så tavle → tidslinje → kart virket og feilen så
 * ut som ingenting. Å bytte til standarden returnerte `rest` — som fortsatt bar den GAMLE
 * visningen — så «Liste» skrev `?view=board` tilbake i adressen og tavla ble stående. Målt i
 * nettleseren 19. september på begge rutene før rettingen.
 *
 * `view === DEFAULT_VIEW ? rest` leser som «uten visning». Det er bare sant når `view` faktisk er
 * tatt ut av `rest` først, og det er hele rettingen.
 */
export function withView(search: TasksSearch, view: TaskView): TasksSearch {
  const { group: _group, dir: _dir, view: _view, ...rest } = search;
  return view === DEFAULT_VIEW ? rest : { ...rest, view };
}

export function statusesOf(search: TasksSearch): readonly TaskStatus[] {
  return parseStatuses(search.status);
}

/** The order `GET /v1/tasks` is asked for. Always a key the endpoint accepts. */
export function sortOf(search: TasksSearch): { sort: TaskSortKey; direction: SortDirection } {
  return {
    sort: GROUPING_SORT[groupingOf(search)],
    direction: search.dir ?? DEFAULT_DIRECTION,
  };
}

/**
 * What `Table` is handed, so the grouped column wears `aria-sort` and the arrow.
 *
 * `null` for the flat list: no column carries that order, and a header claiming `aria-sort` for
 * an order it does not express is a header that lies. The rows are still in deadline order —
 * the "Frist" column simply is not the reason, the "Ingen gruppering" choice is.
 */
export function tableSortOf(search: TasksSearch): TableSort | null {
  const grouping = groupingOf(search);
  if (grouping === 'none') return null;
  return {
    columnId: GROUPING_COLUMNS[grouping],
    direction: search.dir ?? DEFAULT_DIRECTION,
  };
}

/**
 * The next URL for a header click — and the one place that guarantees a grouping change starts
 * the list again from the top.
 *
 * A keyset cursor is minted for one sort spec and `decodeCursor` refuses it under any other
 * (`packages/shared/src/http/pagination.ts`), so carrying a position across a change of order is
 * not a stale-data problem, it is a 400. Nothing is carried: what comes back holds the scope and
 * the status filter, and a position is not one of them. Should this page ever grow a `?cursor=`,
 * this is the function that has to drop it.
 */
export function withSort(search: TasksSearch, sort: TableSort): TasksSearch {
  const grouping = (Object.keys(GROUPING_COLUMNS) as (keyof typeof GROUPING_COLUMNS)[]).find(
    (candidate) => GROUPING_COLUMNS[candidate] === sort.columnId,
  );
  if (grouping === undefined) return search;
  return withGrouping({ ...search, dir: sort.direction }, grouping);
}

/** The next URL for the "Grupper etter" control. Direction is kept: it is a separate choice. */
export function withGrouping(search: TasksSearch, grouping: TaskGrouping): TasksSearch {
  const next: TasksSearch = { ...search };
  if (grouping === DEFAULT_GROUPING) delete next.group;
  else next.group = grouping;
  if (next.dir === DEFAULT_DIRECTION) delete next.dir;
  return next;
}

export function withScope(search: TasksSearch, scope: TaskScope): TasksSearch {
  const next: TasksSearch = { ...search };
  if (scope === DEFAULT_SCOPE) delete next.scope;
  else next.scope = scope;
  return next;
}

/** Toggles one status in the filter. All five selected is the same list as none, so it clears. */
export function withStatus(search: TasksSearch, status: TaskStatus): TasksSearch {
  const current = statusesOf(search);
  const next = current.includes(status)
    ? current.filter((value) => value !== status)
    : [...current, status];

  const result: TasksSearch = { ...search };
  if (next.length > 0 && next.length < MAX_STATUSES) result.status = next.join(',');
  else delete result.status;
  return result;
}

export function prioritiesOf(search: TasksSearch): readonly TaskPriority[] {
  return parsePriorities(search.priority);
}

/**
 * Arrangementene som er krysset av. Tom liste betyr ALLE, som fravær gjør for hvert annet filter.
 */
export function editionsOf(search: TasksSearch): readonly string[] {
  return search.editions ?? [];
}

/**
 * ÅRET LISTA STÅR I, hvis utvalget peker på nøyaktig ett — og ellers ingenting.
 *
 * To lesere trenger et svar av typen «ett år»: kartsegmentet, fordi et kart alltid er ett års
 * kart (0222), og avdelingsfilteret, som henter årgangens eget tre. Ingen av dem har et svar når
 * leseren har krysset av to festivaler, og «det første i lista» ville vært et gjett.
 *
 * `ORGANISATION_ONLY` er ikke et år. Er den det eneste valget, er dette fortsatt `undefined`:
 * husets egne oppgaver har ingen årgang, og derfor heller ikke et kart eller et avdelingstre.
 */
export function singleEditionOf(search: TasksSearch): string | undefined {
  const chosen = editionsOf(search);
  const only = chosen.length === 1 ? chosen[0] : undefined;
  return only === undefined || only === ORGANISATION_ONLY ? undefined : only;
}

export function departmentOf(search: TasksSearch): string | undefined {
  return search.dept;
}

export function isOverdueOnly(search: TasksSearch): boolean {
  return search.overdue === true;
}

export function searchOf(search: TasksSearch): string | undefined {
  return search.q;
}

/** Toggles one priority. All four selected is the same list as none, so it clears. */
export function withPriority(search: TasksSearch, priority: TaskPriority): TasksSearch {
  const current = prioritiesOf(search);
  const next = current.includes(priority)
    ? current.filter((value) => value !== priority)
    : [...current, priority];

  const result: TasksSearch = { ...search };
  if (next.length > 0 && next.length < MAX_PRIORITIES) result.priority = next.join(',');
  else delete result.priority;
  return result;
}

/**
 * Setter utvalget. En tom liste er «alle», og skrives som FRAVÆR.
 *
 * Det er den samme regelen som for statuser og prioriteter, og den er ikke kosmetisk: en adresse
 * som bærer hele lista ser filtrert ut, og «Fjern filtre» ville ikke hatt noe å fjerne.
 *
 * AVDELINGEN SLIPPES NÅR ÅRET ENDRER SEG. Årgangens «Grill» og malens «Grill» er to rader med
 * hver sin id, så et `?dept=` fra forrige utvalg peker på en gruppe det nye kanskje ikke har:
 * velgeren viser «Alle», lista er tom, og ingenting på skjermen sier hvorfor.
 */
export function withEditions(search: TasksSearch, editions: readonly string[]): TasksSearch {
  const next: TasksSearch = { ...search };
  if (editions.length === 0) delete next.editions;
  else next.editions = editions;
  if (singleEditionOf(next) !== singleEditionOf(search)) delete next.dept;
  return next;
}

export function withDepartment(search: TasksSearch, department: string | undefined): TasksSearch {
  const next: TasksSearch = { ...search };
  if (department === undefined || department === '') delete next.dept;
  else next.dept = department;
  return next;
}

export function withOverdue(search: TasksSearch, overdue: boolean): TasksSearch {
  const next: TasksSearch = { ...search };
  if (overdue) next.overdue = true;
  else delete next.overdue;
  return next;
}

/**
 * Everything except the grouping, the direction and the SCOPE — none of which is a filter.
 *
 * `scope` used to be cleared here, and that made «Nullstill filtre» do something nobody asked for:
 * pressing it on «Alle oppgaver» flipped the visible switch back to «Mine» and returned a list that
 * was SMALLER than the one before. A button that clears narrowings must not narrow.
 *
 * It also disagreed with `isFiltered` one function below, which has never counted the scope — so
 * the two halves of the same idea gave two answers, and the new clear button in the filter row is
 * what made the disagreement visible. The scope is the page's subject, it has a control of its own
 * whose state is on screen, and clearing filters leaves it exactly where the reader put it.
 */
export function withoutFilters(search: TasksSearch): TasksSearch {
  const next: TasksSearch = { ...search };
  delete next.status;
  delete next.priority;
  delete next.editions;
  delete next.dept;
  delete next.overdue;
  delete next.label;
  return next;
}

/**
 * True when the list has been narrowed — which changes the empty copy (DESIGN.md §8).
 *
 * SCOPE IS NOT IN HERE, and that is deliberate rather than an omission: "mine" is the page's
 * subject, not a narrowing of it, and "Ingenting er tildelt deg" is good news that needs no
 * "nullstill filtre" button. Every one of the five that IS here can be cleared away.
 */
export function isFiltered(search: TasksSearch): boolean {
  return (
    statusesOf(search).length > 0 ||
    prioritiesOf(search).length > 0 ||
    editionsOf(search).length > 0 ||
    search.dept !== undefined ||
    search.label !== undefined ||
    isOverdueOnly(search)
  );
}

/* =================================================================================================
 * KOI Del 1 — etiketten, «Min dag» og den åpne oppgaven.
 * ============================================================================================== */

export function labelOf(search: TasksSearch): string | undefined {
  return search.label;
}

export function withLabel(search: TasksSearch, label: string | undefined): TasksSearch {
  const next: TasksSearch = { ...search };
  if (label === undefined || label === '') delete next.label;
  else next.label = label;
  return next;
}

/** Hvilken oppgave panelet står på, eller ingen. */
export function openOf(search: TasksSearch): string | undefined {
  return search.open;
}

/**
 * Åpner og lukker panelet.
 *
 * DEN SKRIVER IKKE OVER NOE ANNET. Å åpne en oppgave er ikke å endre lista bak den — filtrene,
 * grupperingen og visningen står, så den som lukker panelet er nøyaktig der de var. Det er hele
 * forskjellen på et sidepanel og en rute.
 */
export function withOpen(search: TasksSearch, taskId: string | undefined): TasksSearch {
  const next: TasksSearch = { ...search };
  if (taskId === undefined) delete next.open;
  else next.open = taskId;
  return next;
}

/**
 * ÉN status, satt fra en filterpille — eller ingen.
 *
 * Chipsene kunne slå av og på flere om gangen; en `ListToolbar`-pille kan ikke, og det er prisen
 * verktøylinja koster (se `TasksPage`). Parseren tar fortsatt imot en kommaliste, så en gammel
 * lenke med to statuser i seg åpner nøyaktig som før — det er bare KONTROLLEN som er smalere enn
 * modellen, og det er den riktige veien rundt.
 */
export function withOnlyStatus(search: TasksSearch, status: string): TasksSearch {
  const next: TasksSearch = { ...search };
  if (status === '' || !isStatus(status)) delete next.status;
  else next.status = status;
  return next;
}

export function withOnlyPriority(search: TasksSearch, priority: string): TasksSearch {
  const next: TasksSearch = { ...search };
  if (priority === '' || !isPriority(priority)) delete next.priority;
  else next.priority = priority;
  return next;
}

/**
 * LESEMÅTEN, UTEN STEDET (QA13 runde 2, K-18 og J9).
 *
 * =================================================================================================
 * Atle: «Visningsvalg (hvilke arrangementer, filtre, gruppering, valgt visning) lagres per bruker
 * og speiles i URL-en.»
 *
 * Det som lagres er hvordan man LESER lista — visning, gruppering, retning, filtre. Det som IKKE
 * lagres er hvor man er: `open` er en oppgave noen har åpnet, og `q` er et søk man gjorde én gang.
 * Å huske dem ville betydd at kalenderen åpnet seg på gårsdagens søk hver morgen.
 *
 * Det er den samme grensen `keepFilters` trekker i kalenderen, og av den samme grunnen: et filter
 * er en LESEMÅTE og følger med; et sted er et sted og gjør det ikke.
 */
export function keepTaskView(search: TasksSearch): TasksSearch {
  const kept: TasksSearch = {};
  if (search.view !== undefined) kept.view = search.view;
  if (search.group !== undefined) kept.group = search.group;
  if (search.dir !== undefined) kept.dir = search.dir;
  if (search.scope !== undefined) kept.scope = search.scope;
  if (search.status !== undefined) kept.status = search.status;
  if (search.priority !== undefined) kept.priority = search.priority;
  if (search.editions !== undefined) kept.editions = search.editions;
  if (search.dept !== undefined) kept.dept = search.dept;
  if (search.overdue === true) kept.overdue = true;
  return kept;
}
